/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import { fragment } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type NotificationType from '@linc-technologies/linced-common/enums/notification-type';
import type Notification from '@linc-technologies/linced-common/models/notifications/notification';
import type TrackingInfo from 'linced-app/models/notifications/tracking-info';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';

// TODO: Move this to linced-common
// Proto: notifications.Result.
export default class Result extends Model {
  declare [Type]: 'notifications/result';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId contains the unique school identifier of the school who caused
  // the delivery result to occur.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId contains the unique school campus identifier of the school's
  // campus who caused the delivery result to occur.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // notificationId provides a reference back to the original notification
  // that created the delivery result to occur.
  @belongsTo('notifications/notification', { async: true, inverse: null })
  declare notificationId: AsyncBelongsTo<Notification>;

  @alias('notificationId')
  declare notification: AsyncBelongsTo<Notification>;

  // notificationType contains the type of notification that was generated by
  // the system.
  @attr('number', { defaultValue: 0 })
  declare notificationType: EnumValue<typeof NotificationType>;

  // entityId holds a reference based on the notification type.
  // For example:
  //   If notificationType = 1, entityId points to maps to the id of a
  //   community notice.
  @attr('string', { defaultValue: '' })
  declare entityId: string;

  // personId contains the unique identifier of the person the notification
  // was attempted to be delivered to.
  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.type')
  declare personType: unknown;

  // transportType specifies the underlying mode of delivery that was used to
  // attempt delivery of the notification. For example, "mandrill"
  @attr('string', { defaultValue: '' })
  declare transportType: string;

  // isRead marks whether the sent notification has been read.
  @attr('boolean', { defaultValue: false })
  declare isRead: boolean;

  // deliveryStatus provides a way to inform the client of the outcome
  // returned from the transports delivery as enumerated values.
  @attr('number', { defaultValue: 0 })
  declare status: number;

  // StatusError provides a string field for transports to store error
  // information to help the end user understand why delivery failed.
  @attr('string', { defaultValue: '' })
  declare statusError: string;

  // scheduleTime contains the time the message is scheduled to be delivered,
  // assuming the notification has been scheduled for delivery.
  @attr('number')
  declare scheduleTime: number | undefined;

  // trackingId holds a reference to the external providers unique record
  // identifier.
  @attr('string', { defaultValue: '' })
  declare trackingId: string;

  // TrackingStatus provides a way to inform the client of the outcome
  // returned from the transports message tracking as enumerated values.
  @attr('number', { defaultValue: 0 })
  declare trackingStatus: number;

  // TrackingError provides a string field for transports to store extra
  // delivery and or tracking error information to help the end user
  // understand why tracking failed at a deeper level.
  @attr('string', { defaultValue: '' })
  declare trackingError: string;

  // TrackingInformation provides a construct in which meta information can
  // be pushed into. For example, the mandrill transport returns much deeper
  // tracking information. Therefore, if the transport is mandrill this extra
  // information will be present in the notification result.
  @fragment('notifications/tracking-info')
  declare trackingInfo: TrackingInfo | null;
}
